import { useEffect, useRef, useState, type JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Segmented } from "../../design/components/Segmented";
import { formatCount } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { useLeadView } from "../lead-queries";
import { dataIssuesQuery, duplicatesQuery } from "./cleanup-queries";
import { DataIssuesTab } from "./DataIssuesTab";
import { DuplicatesTab } from "./DuplicatesTab";
import "./cleanup.css";

// «Pulizia dati» (piano M3 Task 18, D11 e D12; mock-up `lead.html`, la finestra `m-clean-dialog` di `initLeadPage`): in cima
// la spiegazione del mock-up, poi le due schede «Doppioni (N)» e «Telefoni ed email (N)» (la scheda scelta sta
// nell'indirizzo, `pulizia=doppioni|telefoni`, così «Indietro» dalla scheda di un Lead la ritrova), con i numeri delle
// stesse letture del pulsante dell'elenco. Niente cambia senza un clic: ogni unione e ogni correzione aspetta l'OK. L'esito
// di ciò che si fa qui si legge nel piede della finestra e prende il focus, perché il pulsante premuto sparisce con la sua
// riga e una finestra modale rende inerte la pagina, avvisi compresi (D16).

export type CleanupTab = "doppioni" | "telefoni";

/** L'esito di un'azione: una frase riuscita (`success`) oppure un problema da leggere (`warning`). */
export type CleanupOutcome = { tone: "success" | "warning"; text: string };

type Props = {
  tab: TabContext;
  current: CleanupTab;
  onTab: (next: CleanupTab) => void;
  timeZone: string;
  /** La funzione «Cambiare l'owner, con il motivo»: senza, l'owner dell'altro Lead non si sceglie (il server rifiuterebbe). */
  canChangeOwner: boolean;
  onClose: () => void;
};

export function DataCleanupDialog({ tab, current, onTab, timeZone, canChangeOwner, onClose }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const view = useLeadView();
  // Aprire la finestra rilegge i due numeri, anche se il pulsante li ha letti da meno di un minuto (decisione P56): la
  // finestra parte dai numeri di adesso, e il pulsante dell'elenco li riceve con lei.
  const duplicates = useQuery({ ...duplicatesQuery(tab, { offset: 0, limit: 1 }, view), refetchOnMount: "always" });
  const issues = useQuery({ ...dataIssuesQuery(tab, { offset: 0, limit: 1 }, view), refetchOnMount: "always" });
  const [outcome, setOutcome] = useState<CleanupOutcome | null>(null);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const problemRef = useRef<HTMLParagraphElement>(null);
  const focusOutcome = useRef(false);

  // Dopo un'azione il focus va sull'esito, al disegno che segue: il pulsante premuto sparisce con la sua coppia o riga.
  useEffect(() => {
    if (!focusOutcome.current) return;
    focusOutcome.current = false;
    (outcome?.tone === "warning" ? problemRef : statusRef).current?.focus();
  });

  function report(next: CleanupOutcome | null) {
    setOutcome(next);
    focusOutcome.current = next !== null;
  }

  function choose(next: string) {
    if (next !== "doppioni" && next !== "telefoni") return;
    setOutcome(null);
    onTab(next);
  }

  const options = [
    {
      value: "doppioni",
      label: duplicates.data ? t("dataCleanup.tabs.duplicates", { count: formatCount(locale, duplicates.data.total) }) : t("dataCleanup.tabs.duplicatesPlain"),
    },
    { value: "telefoni", label: issues.data ? t("dataCleanup.tabs.issues", { count: formatCount(locale, issues.data.total) }) : t("dataCleanup.tabs.issuesPlain") },
  ];

  const footer = (
    <>
      <p ref={statusRef} tabIndex={-1} className="g-sheet__status g-cleanup__outcome" role="status">
        {outcome?.tone === "success" ? outcome.text : null}
      </p>
      {outcome?.tone === "warning" ? (
        <p ref={problemRef} tabIndex={-1} className="g-field__error g-cleanup__outcome" role="alert">
          {outcome.text}
        </p>
      ) : null}
      <Button onClick={onClose}>{t("common.close")}</Button>
    </>
  );

  return (
    <Dialog open size="table" title={t("dataCleanup.title")} onClose={onClose} footer={footer}>
      <p className="g-cleanup__intro">{t("dataCleanup.intro")}</p>
      <Segmented label={t("dataCleanup.tabsLabel")} options={options} value={current} onChange={choose} />
      {current === "doppioni" ? (
        <DuplicatesTab tab={tab} view={view} timeZone={timeZone} canChangeOwner={canChangeOwner} onOutcome={report} />
      ) : (
        <DataIssuesTab tab={tab} view={view} onOutcome={report} />
      )}
    </Dialog>
  );
}
